/**
 * Canonical Supabase tables for electricity / natural gas consumption (Python pipelines:
 * energy_elec_places.py, energy_ng_places.py, upsert_energy_tables.py).
 * Canonical consumption tables: elec_*_format / ng_*_format (legacy db_elec_* / db_ng_* superseded).
 */
export const ELEC_RES_FORMAT = 'elec_res_format';
export const ELEC_COM_FORMAT = 'elec_com_format';
export const NG_RES_FORMAT = 'ng_res_format';
export const NG_COM_FORMAT = 'ng_com_format';

export type EnergyFormatTable =
  | typeof ELEC_RES_FORMAT
  | typeof ELEC_COM_FORMAT
  | typeof NG_RES_FORMAT
  | typeof NG_COM_FORMAT;

export function sectorForEnergyFormatTable(table: EnergyFormatTable): 'Residential' | 'Commercial' {
  return table === ELEC_RES_FORMAT || table === NG_RES_FORMAT ? 'Residential' : 'Commercial';
}

/**
 * Maps population / GHG inventory geography_type (City, County, State) to values
 * used in *_format rows (Python mixes city, County, place, etc.).
 */
export function formatGeographyTypesForInventory(geographyType: string): string[] {
  const lower = geographyType.trim().toLowerCase();
  if (lower === 'county') return ['County', 'county'];
  // Prefer canonical `place` rows (energy_ng_places / utility therms) before legacy `city` model stack.
  if (lower === 'city') return ['place', 'Place', 'city', 'City'];
  if (lower === 'state') return ['State', 'state'];
  return [geographyType.trim()];
}

/** Geofips variants for TEXT/BIGINT columns and leading-zero differences. */
export function geofipsVariantsForQuery(geofips: number): (string | number)[] {
  const s = String(geofips);
  const out = new Set<string | number>();
  out.add(geofips);
  out.add(s);
  const stripped = s.replace(/^0+/, '') || '0';
  if (stripped !== s) {
    out.add(stripped);
    const n = Number(stripped);
    if (Number.isFinite(n)) out.add(n);
  }
  return Array.from(out);
}

/**
 * Single geography_type string for upserts into *_format (Python uses `city` / `County` / `state` in places).
 */
export function primaryGeographyTypeForFormatUpsert(populationGeographyType: string): string {
  const l = populationGeographyType.trim().toLowerCase();
  if (l === 'city') return 'city';
  if (l === 'county') return 'County';
  if (l === 'state') return 'state';
  return populationGeographyType.trim();
}
